import { Button, Field, Input, NativeSelect, Stack, VStack } from '@chakra-ui/react'
import { useState } from 'react'
import { useCampaigns } from '../../hooks/useCampaign'
import { useLobbyDayEvents } from '../../hooks/useLobbyDay'
import { ContactSearchSelect } from '../shared/ContactSearchSelect'
import type { ReportDefinition, ReportParameterDef } from '../../types/reporting'

interface Props {
  definition: ReportDefinition
  onSubmit: (parameters: Record<string, string>) => void
  isBusy: boolean
}

interface FieldProps {
  param: ReportParameterDef
  value: string
  onChange: (value: string) => void
}

// Campaign and LobbyDayEvent parameters are foreign keys, not free text — nobody should have to
// type a GUID. Each gets its own small component (rather than an inline branch) purely so its
// data-fetching hook only mounts, and only ever calls useQuery, when a report actually asks for
// that parameter kind; a report with no Campaign/LobbyDayEvent parameter never touches either
// query, which also keeps this form usable in tests without a QueryClientProvider.
//
// Three states, not two: loading, loaded-but-empty, and loaded-with-options. A brand-new
// committee has zero campaigns and zero lobby day events on day one — an enabled dropdown with
// nothing but the placeholder in it silently blocks submission (if the parameter is required)
// with no indication why. The empty state disables the field and says so, same idiom as loading
// (placeholder-text swap + disabled) rather than a separate banner or a new component.
function CampaignField({ param, value, onChange }: FieldProps) {
  const { data: campaigns, isLoading } = useCampaigns()
  const id = `param-${param.key}`
  const isEmpty = !isLoading && (campaigns ?? []).length === 0
  const placeholder = isLoading
    ? 'Loading campaigns…'
    : isEmpty
      ? 'No campaigns yet — create one first'
      : 'Select a campaign'
  return (
    // NativeSelect.Root's own `disabled` prop is overridden by an ancestor Field.Root's field
    // context (see @chakra-ui/react's native-select.js: `field?.disabled ?? props.disabled` —
    // Field.Root always supplies a non-nullish `disabled`, so it wins over NativeSelect.Root's
    // prop whenever nested inside one, as it is here). Field.Root's own `disabled` is the prop
    // that actually reaches the rendered <select>.
    <Field.Root required={param.required} disabled={isLoading || isEmpty}>
      <Field.Label htmlFor={id}>{param.label}</Field.Label>
      <NativeSelect.Root>
        <NativeSelect.Field
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {(campaigns ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Field.Root>
  )
}

function LobbyDayEventField({ param, value, onChange }: FieldProps) {
  const { data: events, isLoading } = useLobbyDayEvents()
  const id = `param-${param.key}`
  const isEmpty = !isLoading && (events ?? []).length === 0
  const placeholder = isLoading
    ? 'Loading events…'
    : isEmpty
      ? 'No lobby day events yet — create one first'
      : 'Select an event'
  return (
    // See the identical comment on CampaignField above — Field.Root's `disabled` is the one that
    // reaches the rendered <select> here, not NativeSelect.Root's.
    <Field.Root required={param.required} disabled={isLoading || isEmpty}>
      <Field.Label htmlFor={id}>{param.label}</Field.Label>
      <NativeSelect.Root>
        <NativeSelect.Field
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {(events ?? []).map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.name}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Field.Root>
  )
}

// L11 slice 3c: the Contact parameter kind (the disclosure report's reverse lookup, "who has ever
// held a list containing this person"). Unlike Campaign/LobbyDayEvent there is no bounded list to
// put in a <select> -- a committee has voter-scale contacts -- so this reuses the shared
// debounced-typeahead ContactSearchSelect, which hits the already-capped, already-audited contact
// search endpoint rather than fetching a list of its own.
function ContactField({ param, value, onChange }: FieldProps) {
  const [displayName, setDisplayName] = useState('')
  return (
    <Field.Root required={param.required}>
      <Field.Label>{param.label}</Field.Label>
      <ContactSearchSelect
        value={value || undefined}
        displayName={displayName}
        onSelect={(contactId, name) => {
          onChange(contactId ?? '')
          setDisplayName(name ?? '')
        }}
        placeholder="Search for a person..."
        // Whole-branch review fix (F11): ContactSearchSelect defaults to 'sm' (its other callers
        // are compact inline pickers), but every sibling field in this form -- CampaignField's
        // NativeSelect, LobbyDayEventField's, the plain Input fallback -- renders at Chakra's
        // 'md' default. Pass it through explicitly so this field doesn't render smaller than its
        // neighbours.
        size="md"
      />
    </Field.Root>
  )
}

export function ReportParameterForm({ definition, onSubmit, isBusy }: Props) {
  const [values, setValues] = useState<Record<string, string>>({})

  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }))

  // The DateRange kind is the one asymmetric case: the definition advertises a single parameter
  // (key "period"), but the backend contract reads two keys, periodFrom/periodTo, as full ISO
  // instants bracketing the whole day. That is a property of the DateRange *kind*, not of any
  // particular parameter's key, so branch on kind here rather than on p.key.
  const build = (): Record<string, string> | null => {
    const out: Record<string, string> = {}
    for (const p of definition.parameters) {
      if (p.kind === 'DateRange') {
        const from = values[`${p.key}From`]
        const to = values[`${p.key}To`]
        if (!from || !to) return null
        out.periodFrom = `${from}T00:00:00Z`
        out.periodTo = `${to}T23:59:59Z`
      } else {
        const raw = values[p.key]
        if (p.required && !raw) return null
        if (raw) out[p.key] = raw
      }
    }
    return out
  }

  return (
    <VStack align="stretch" gap={4}>
      {definition.parameters.map((p) => {
        if (p.kind === 'DateRange') {
          const fromId = `${p.key}-from`
          const toId = `${p.key}-to`
          return (
            <Stack key={p.key} direction={{ base: 'column', md: 'row' }} gap={3} align={{ base: 'stretch', md: 'end' }}>
              <Field.Root required={p.required}>
                <Field.Label htmlFor={fromId}>Period start</Field.Label>
                <Input
                  id={fromId}
                  type="date"
                  value={values[`${p.key}From`] ?? ''}
                  onChange={(e) => set(`${p.key}From`, e.target.value)}
                />
              </Field.Root>
              <Field.Root required={p.required}>
                <Field.Label htmlFor={toId}>Period end</Field.Label>
                <Input
                  id={toId}
                  type="date"
                  value={values[`${p.key}To`] ?? ''}
                  onChange={(e) => set(`${p.key}To`, e.target.value)}
                />
              </Field.Root>
            </Stack>
          )
        }

        if (p.kind === 'Campaign') {
          return (
            <CampaignField key={p.key} param={p} value={values[p.key] ?? ''} onChange={(v) => set(p.key, v)} />
          )
        }

        if (p.kind === 'LobbyDayEvent') {
          return (
            <LobbyDayEventField key={p.key} param={p} value={values[p.key] ?? ''} onChange={(v) => set(p.key, v)} />
          )
        }

        if (p.kind === 'Contact') {
          return (
            <ContactField key={p.key} param={p} value={values[p.key] ?? ''} onChange={(v) => set(p.key, v)} />
          )
        }

        const id = `param-${p.key}`
        return (
          <Field.Root key={p.key} required={p.required}>
            <Field.Label htmlFor={id}>{p.label}</Field.Label>
            <Input id={id} value={values[p.key] ?? ''} onChange={(e) => set(p.key, e.target.value)} />
          </Field.Root>
        )
      })}
      <Button
        colorPalette="brand"
        alignSelf={{ base: 'stretch', md: 'flex-start' }}
        width={{ base: 'full', md: 'auto' }}
        loading={isBusy}
        onClick={() => {
          const p = build()
          if (p) onSubmit(p)
        }}
      >
        Preview
      </Button>
    </VStack>
  )
}
